import { useEffect, useState } from "react";
import dayjs from "dayjs";
import Select from "react-select";
import clone from "lodash-es/clone";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import Link from "@mui/material/Link";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import Checkbox from "@mui/material/Checkbox";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import AccordionTitle from "../../components/AccordionTitle";

import DrawerAppBar from "../../components/DrawerAppBar";
import ProgressBar from "../../components/ProgressBar";
import Title from "../../components/Title";
import StudentSelector from "../../components/faculty/report/StudentSelector";

import { doService } from "../../services/ServiceHelper";
import { getSchoolGrades } from "../../services/SchoolService";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";
import { TableHead } from "@mui/material";

import {
  getScoresWithoutFulfillments,
  getStudentFulfillments,
} from "../../services/ReportService";
import { getScoreConsequences } from "../../services/ScoreService";
import { showError, showPrompt, showToast } from "../../components/ATPDialogs";
import { saveFulfillment, saveFulfillments } from "../../services/ScoreService";

/**
 *
 */
const ConsequenceFulfillment = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);
  const [_grades, setGrades] = useState([]);
  const [_dateRange, setDateRange] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);

  const [_searchPerformed, setSearchPerformed] = useState(false);
  const [_selectedGrades, setSelectedGrades] = useState([]);
  const [_consequences, setConsequences] = useState([]);
  const [_selectedConsequenceEids, setSelectedConsequenceEids] = useState([]);
  const [_results, setResults] = useState([]);
  const [_selectedStudentEid, setSelectedStudentEid] = useState("XX");
  const [_studentOpen, setStudentOpen] = useState(false);
  const [_selectedStudent, setSelectedStudent] = useState({});
  const [_studentFulfillments, setStudentFulfillments] = useState([]);

  useEffect(() => {
    initConsequences();
    initSchoolGrades();
  }, []);

  const initConsequences = () => {
    doService(getScoreConsequences(_school, _jwt), (results) => {
      const entries = [];
      const map = {};
      entries.push({
        label: "-- ALL CONSEQUENCES --",
        value: "XX",
      });
      for (const entry of results.data) {
        entries.push({
          label: entry.name,
          value: entry.eid,
        });
        map["c" + entry.eid] = entry;
      }
      setConsequences(entries);
    });
  };

  const initSchoolGrades = () => {
    setShowProgress(true);
    doService(getSchoolGrades(_school, _jwt), (results) => {
      const labeledGrades = [];
      labeledGrades.push({
        label: "-- ALL GRADES --",
        value: "XX",
      });
      for (const grade of results.data.grades) {
        labeledGrades.push({
          label: `${grade}`,
          value: grade,
        });
      }
      setGrades(labeledGrades);
      setShowProgress(false);
    });
  };

  const handleChangeDateRange = (value) => {
    setDateRange(value);
  };

  const handleSearch = () => {
    const startDate = _dateRange[0].format("YYYY-MM-DD");
    const endDate = _dateRange[1].format("YYYY-MM-DD");

    if (_selectedGrades.length === 0 && _selectedStudentEid === "XX") {
      showError(
        "Unable to Search",
        "Please select 1 or more grades, or a student."
      );
      return;
    }
    if (_selectedConsequenceEids.length === 0) {
      showError("Unable to Search", "Please select 1 or more consequences.");
      return;
    }

    let gradesArry = [];
    for (const entry of _selectedGrades) {
      if (entry.value === "XX") {
        gradesArry = [];
        break;
      }
      gradesArry.push(entry.value);
    }
    let gradesStr = gradesArry.join(",");

    let consequenceEidsArry = [];
    for (const entry of _selectedConsequenceEids) {
      if (entry.value === "XX") {
        consequenceEidsArry = [];
        break;
      }
      consequenceEidsArry.push(entry.value);
    }
    let consequenceEidsStr = consequenceEidsArry.join(",");

    setShowProgress(true);
    doService(
      getScoresWithoutFulfillments(
        _school,
        _jwt,
        startDate,
        endDate,
        gradesStr,
        consequenceEidsStr,
        _selectedStudentEid
      ),
      (results) => {
        setShowProgress(false);
        setSearchPerformed(true);

        for (const result of results.data) {
          result.checked = false;
        }
        setResults(results.data);
      }
    );
  };

  const onChangeChecked = (event, selectedUnf) => {
    const results = clone(_results);
    for (const result of results) {
      if (result.scoreEid === selectedUnf.scoreEid) {
        result.checked = !result.checked;
        break;
      }
    }
    setResults(results);
  };

  const onClickFulfill = (con) => {
    showPrompt("Enter Comment.", "", "", (comment) => {
      doFulfill(con.scoreEid, comment);
    });
  };

  const doFulfill = (scoreEid, comment) => {
    setShowProgress(true);
    doService(saveFulfillment(_school, _jwt, scoreEid, comment), (results) => {
      setShowProgress(false);
      handleSearch();
      showToast("S", "Fullfillment Saved.");
    });
  };

  const onClickFulfillSelected = () => {
    const scoreEids = [];

    for (const result of _results) {
      if (result.checked === true) {
        scoreEids.push(result.scoreEid);
      }
    }

    if (scoreEids.length === 0) {
      showError("Unable to Fulfill", "Please check 1 or more rows.");
      return;
    }

    // apply one comment for all checked items
    showPrompt(
      `Enter Comment for ${scoreEids.length} item(s).`,
      "",
      "",
      (comment) => {
        setShowProgress(true);
        doService(
          saveFulfillments(_school, _jwt, scoreEids, comment),
          (results) => {
            setShowProgress(false);
            handleSearch();
            showToast("S", "Fullfillment(s) Saved.");
          }
        );
      }
    );
  };
  const onSelectStudent = (value) => setSelectedStudentEid(value);

  const onClickStudent = (consequence) => {
    setShowProgress(true);
    setSelectedStudent(consequence);
    doService(
      getStudentFulfillments(_school, _jwt, consequence.studentEid),
      (results) => {
        setShowProgress(false);
        setStudentFulfillments(results.data);
        setStudentOpen(true);
      }
    );
  };

  const handleCloseStudent = () => {
    setStudentOpen(false);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Consequence Fulfillment" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        <Alert severity="info">
          Use the Search Criteria to find Consequences that have not been
          fulfilled by the student.
        </Alert>

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text="Search Criteria" />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 0 }}>Date Range: *</InputLabel>
                <MobileDateRangePicker
                  value={_dateRange}
                  sx={{ mt: 1, mb: 1 }}
                  onChange={handleChangeDateRange}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 0 }}>Grade(s): *</InputLabel>
                <Select
                  options={_grades}
                  onChange={(value) => setSelectedGrades(value)}
                  isMulti={true}
                  value={_selectedGrades}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 0 }}>Consequence(s): *</InputLabel>
                <Select
                  options={_consequences}
                  isMulti={true}
                  onChange={(values) => setSelectedConsequenceEids(values)}
                  defaultValue={_selectedConsequenceEids}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Student (optional)</InputLabel>
                <StudentSelector
                  school={_school}
                  jwt={_jwt}
                  onChange={onSelectStudent}
                />
              </Grid>
            </Grid>

            <br />
            <Button
              sx={{ mt: 0.5 }}
              variant="contained"
              fullWidth
              onClick={handleSearch}
              className="button1"
            >
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        {_searchPerformed === true && (
          <>
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle text={`Student List (${_results.length})`} />
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 1 }}>
                  {" "}
                  If you use the checkboxes to select multiple students, please
                  make sure to click the "Fulfill Checked Rows" button.
                </Alert>
                <Button
                  variant="contained"
                  className="button1"
                  onClick={onClickFulfillSelected}
                >
                  Fulfill Checked Rows
                </Button>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell align="center" width={80}>
                          Select?
                        </StripedTableCell>{" "}
                        <StripedTableCell align="center" width={80}>
                          Con Date
                        </StripedTableCell>
                        <StripedTableCell>Student</StripedTableCell>
                        <StripedTableCell align="center">
                          Grade
                        </StripedTableCell>
                        <StripedTableCell>Score Category</StripedTableCell>
                        <StripedTableCell>Consequence</StripedTableCell>
                        <StripedTableCell align="center">Archived?</StripedTableCell>
                        <StripedTableCell align="center">
                          Actions
                        </StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults data={_results} />
                      {_results.map((con) => (
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            <Checkbox
                              sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}
                              onChange={(event) => onChangeChecked(event, con)}
                              checked={con.checked}
                            />
                            {con.checked}
                          </StripedTableCell>

                          <StripedTableCell align="center">
                            {dayjs(con.scoreDate).format("MM/DD")}
                          </StripedTableCell>
                          <StripedTableCell>
                            <Link
                              component="button"
                              onClick={() => onClickStudent(con)}
                            >
                              {con.studentLastName}, {con.studentFirstName}
                            </Link>
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            {con.grade}
                          </StripedTableCell>
                          <StripedTableCell>{con.catName}</StripedTableCell>
                          <StripedTableCell>{con.conName}</StripedTableCell>
                          <StripedTableCell align="center">
                            {con.dateArchived ? dayjs(con.dateArchived).format("MM/DD") : "No"}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            <Button
                              variant="contained"
                              onClick={() => onClickFulfill(con)}
                            >
                              Fulfill
                            </Button>
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>

      <Dialog
        onClose={handleCloseStudent}
        open={_studentOpen}
        PaperProps={{
          sx: {
            width: 800,
            maxWidth: 800,
            maxHeight: 400,
          },
        }}
      >
        <DialogTitle>
          Fulfillments for {_selectedStudent.studentFirstName}{" "}
          {_selectedStudent.studentLastName}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell align="center">Score Date</StripedTableCell>
                  <StripedTableCell>Category</StripedTableCell>
                  <StripedTableCell>Consequence</StripedTableCell>
                  <StripedTableCell align="center">
                    Fulfilled Date
                  </StripedTableCell>
                  <StripedTableCell>
                    Fulfilled By
                  </StripedTableCell>
                  <StripedTableCell align="center">
                    Deleted Date
                  </StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_studentFulfillments} />
                {_studentFulfillments.map((f) => (
                  <StripedTableRow>
                    <StripedTableCell align="center">
                      {dayjs(f.scoreDate).format("MM/DD")}
                    </StripedTableCell>
                    <StripedTableCell>{f.catName}</StripedTableCell>
                    <StripedTableCell>{f.conName}</StripedTableCell>
                    <StripedTableCell align="center">{dayjs(f.fulfillDateCreated).format("MM/DD")}</StripedTableCell>
                    <StripedTableCell>{f.fulfillerLastName}, {f.fulfillerFirstName}</StripedTableCell>
                    <StripedTableCell align="center">{f.scoreDateDeleted != null ? dayjs(f.scoreDateDeleted).format("MM/DD"): "N/A"}</StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseStudent} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsequenceFulfillment;
